// eslint-disable-next-line node/no-unpublished-import
import deparam from 'deparam';
import { mergeDeep } from './jgraduate/Util.js';
import Editor from './Editor.js';

/**
  * Preferences.
  * @todo `save_notice_done` and `export_notice_done` should be changed to flags rather than preferences
*/
export interface ISVGEditPreferences {
    /** Two-letter language code. The language must exist in the Editor Preferences language list. Defaults to "en" if `locale.js` detection does not detect another language. */
    lang?:string;
    /** Size of the toolbar icons. Will default to 's' if the window height is smaller than the minimum height and 'm' otherwise. */
    iconsize?:string;
    /** Color hex for canvas background color. Defaults to white. */
    bkgd_color?:string;
    /** Background raster image URL. This image will fill the background of the document; useful for tracing purposes. */
    bkgd_url?:string;
    /** Defines whether included raster images should be saved as Data URIs when possible, or as URL references. Settable in the Document Properties dialog. */
    img_save?:string;
    /** Used to track alert status */
    save_notice_done?:boolean;
    /** Used to track alert status */
    export_notice_done?:boolean;
    /** Mousewheel zoom enabled */
    mouseWheelZoomEnabled?: boolean;
    /** Whether you can click through groups to underlying elements */
    clickThroughGroups?: boolean;
}

/**
  * ConfigOptions
 */
export interface ISVGEditConfiguration {
    [key: string]: any;

    /** The minimum area visible outside the canvas, as a multiple of the image dimensions. The larger the number, the more one can scroll outside the canvas. */
    canvas_expansion?: number;
    /** Init fill properties */
    initFill?: {
        /** The initial fill color. Must be a hex code string. Defaults to solid red. */
        color?: string,
        /** The initial fill opacity. Must be a number between 0 and 1 */
        opacity?: number,
    };
    /** Init stroke properties */
    initStroke?: {
        /** The initial stroke width. Must be a positive number. */
        width?: number,
        /** The initial stroke color. Must be a hex code. Defaults to solid black. */
        color?: string,
        /** The initial stroke opacity. Must be a number between 0 and 1. */
        opacity?: number
    };
    /** Text style properties */
    text?: {
        /** Text stroke width */
        stroke_width?: number,
        /** Text font size */
        font_size?: number,
        /** Text font family */
        font_family?: string
    };
    /** Initial opacity (multiplied by 100) */
    initOpacity?: number;
    /** The initially selected tool. Must be either the ID of the button for the tool, or the ID without `tool_` prefix (e.g., "select"). */
    initTool?: string;
    /**  Can be "new" or "same" to indicate whether new windows will be generated for each export; the `window.name` of the export window is namespaced based on the `canvasName` (and incremented if "new" is selected as the type). Introduced 2.8. */
    exportWindowType?: "new" | "same";
    /** Start in wireframe mode */
    wireframe?: boolean;
    /** Open the layers side-panel by default. */
    showlayers?: boolean;
    /** If `true`, prevents the warning dialog box from appearing when closing/reloading the page. Mostly useful for testing. */
    no_save_warning?: boolean;
    /** The path where the SVG icons are located, with trailing slash. Note that as of version 2.7, this is not configurable by URL for security reasons. */
    imgPath?: string;
    /** The default width/height of a new document. Use an array in `setConfig` (e.g., `[800, 600]`) and comma separated numbers in the URL. */
    dimensions?: [number, number];
    /**  Enable snap to grid by default. Set in Editor Options. */
    gridSnapping?: boolean;
    /** Accepts hex, e.g., '#000'. Set in Editor Options. Defaults to black. */
    gridColor?: string;
    /** Set the default grid snapping value. Set in Editor Options. */
    snappingStep?: number;
    /** Set in Editor Options. */
    baseUnit?: string;
    /**  Initial state of ruler display (v2.6). Set in Editor Options. */
    showRulers?: boolean;
    /** Set to `true` to override the ability for URLs to set non-content configuration (including extension config). Must be set early, i.e., in `svgedit-config-iife.js`; extension loading is too late! */
    preventAllURLConfig?: boolean;
    /** Set to `true` to override the ability for URLs to set URL-based SVG content. Must be set early, i.e., in `svgedit-config-iife.js`; extension loading is too late! */
    preventURLContentLoading?: boolean;
    /** Set by `ext-grid.js`; determines whether or not to show the grid by default */
    showGrid?: boolean;
    /** Used by `ext-xdomain-messaging.js` to indicate which origins are permitted for cross-domain messaging (e.g., between the embedded editor and main editor code). Besides explicit domains, one might add '*' to allow all domains (not recommended for privacy/data integrity of your user's content!), `window.location.origin` for allowing the same origin (should be safe if you trust all apps on your domain), 'null' to allow `file:///` URL usage */
    allowedOrigins?: string[];
    /** This was available via URL only. Allowed an un-encoded URL within the query string (use "url" or "source" with a data: URI instead) */
    paramurl?: string;
    /** Defines whether or not elements outside the canvas should be visible. Set and used in `svgcanvas.ts`. */
    show_outside_canvas?: boolean;
    /** If true, will replace the selection with the current element and automatically select element objects (when not in "path" mode) after they are created, showing their grips (v2.6). Set and used in `svgcanvas.ts` (`mouseUp`). */
    selectNew?: boolean;
}

/**
 * @class configObj
 */
export default class ConfigObj {
    defaultPrefs: ISVGEditPreferences;
    curPrefs: ISVGEditPreferences = {};

    defaultConfig: ISVGEditConfiguration;
    curConfig: ISVGEditConfiguration;

    urldata: any = {};
    editor: Editor;

    // TODO: Fix as any
    preferences: any;

    /**
     * @param {PlainObject} editor
     */
    constructor(editor) {
        this.defaultPrefs = {
            // EDITOR OPTIONS (DIALOG)
            /**
              * Default to "en" if locale.js detection does not detect another language.
              */
            lang: 'en',
            /**
              * Will default to 's' if the window height is smaller than the minimum
              * height and 'm' otherwise.
              */
            iconsize: '',
            bkgd_color: 'chessboard',
            bkgd_url: '',
            // DOCUMENT PROPERTIES (DIALOG)
            img_save: 'embed',
            // ALERT NOTICES
            // Only shows in UI as far as alert notices, but useful to remember, so keeping as pref
            save_notice_done: false,
            export_notice_done: false,
            mouseWheelZoomEnabled: true,
            clickThroughGroups: false,
        };

        this.defaultConfig = {
            canvas_expansion: 3,
            initFill: {
                color: 'FF0000', // solid red
                opacity: 1
            },
            initStroke: {
                width: 5,
                color: '000000', // solid black
                opacity: 1
            },
            text: {
                stroke_width: 0,
                font_size: 24,
                font_family: 'avenir'
            },
            initOpacity: 1,
            initTool: 'select',
            exportWindowType: 'new', // 'same' (todo: also support 'download')
            wireframe: false,
            showlayers: false,
            no_save_warning: false,
            // PATH CONFIGURATION
            // The following path configuration items are disallowed in the URL (as should any future path configurations)
            imgPath: './images/',
            // DOCUMENT PROPERTIES
            // Change the following to a preference (already in the Document Properties dialog)?
            dimensions: [640, 480],
            // EDITOR OPTIONS
            // Change the following to preferences (already in the Editor Options dialog)?
            gridSnapping: false,
            gridColor: '#000',
            baseUnit: 'mm',
            snappingStep: 1,
            showRulers: true,
            // URL BEHAVIOR CONFIGURATION
            preventAllURLConfig: false,
            preventURLContentLoading: false,
            // EXTENSION-RELATED (GRID)
            showGrid: false, // Set by ext-grid.js
        };

        this.curConfig = {
            userExtensions: [],
            /**
            * Can use `location.origin` to indicate the current
            * origin. Can contain a '*' to allow all domains or 'null' (as
            * a string) to support all `file:///` URLs. Cannot be set by
            * URL for security reasons (not safe, at least for
            * privacy or data integrity of SVG content).
            * Might have been fairly safe to allow
            *   `new URL(location.href).origin` by default but
            *   avoiding it ensures some more security that even third
            *   party apps on the same domain also cannot communicate
            *   with this app by default.
            * For use with `ext-xdomain-messaging.js`
            * @todo We might instead make as a user-facing preference.
            */
            allowedOrigins: []
        };
        this.editor = editor;
    }
    /**
     * @function setupCurPrefs
     * @returns {void}
     */
    setupCurPrefs() {
        const curPrefs = { ...this.defaultPrefs, ...this.curPrefs }; // Now safe to merge with priority for curPrefs in the event any are already set
        // Export updated prefs
        this.curPrefs = curPrefs;
    }
    /**
     * Sets up current config based on defaults.
     * @returns {void}
     */
    setupCurConfig() {
        const curConfig = { ...this.defaultConfig, ...this.curConfig }; // Now safe to merge with priority for curConfig in the event any are already set

        // Export updated config
        this.curConfig = curConfig;
    }
    /**
     * @function loadFromURL Load config/data from URL if given
     * @returns {void}
     */
    loadFromURL() {
        const { search, searchParams } = new URL(location.href); // TODO: Fix as any

        if (search) {
            this.urldata = deparam(searchParams.toString());

            ['initStroke', 'initFill'].forEach((prop) => {
                if (searchParams.has(`${prop}[color]`)) {
                    // Restore back to original non-deparamed value to avoid color
                    //  strings being converted to numbers
                    this.urldata[prop].color = searchParams.get(`${prop}[color]`);
                }
            });

            if (searchParams.has('bkgd_color')) {
                this.urldata.bkgd_color = '#' + searchParams.get('bkgd_color');
            }

            if (this.urldata.dimensions) {
                this.urldata.dimensions = this.urldata.dimensions.split(',');
            }

            // Disallowing extension paths via URL for
            // security reasons, even for same-domain
            // ones given potential to interact in undesirable
            // ways with other script resources
            ['userExtensions', 'imgPath']
                .forEach((pathConfig) => {
                    if (this.urldata[pathConfig]) {
                        delete this.urldata[pathConfig];
                    }
                });

            // Note: `source` and `url` are not set on config but are used below
            this.setConfig(this.urldata, { overwrite: false });
            this.setupCurConfig();

            if (!this.curConfig.preventURLContentLoading) {
                let { source } = this.urldata;
                if (!source) { // urldata.source may have been null if it ended with '='
                    const src = searchParams.get('source');
                    if (src && src.startsWith('data:')) {
                        source = src;
                    }
                }
                if (source) {
                    if (source.startsWith('data:')) {
                        this.editor.loadFromDataURI(source);
                    } else {
                        this.editor.loadFromString(source);
                    }
                    return;
                }
                if (this.urldata.url) {
                    this.editor.loadFromURL(this.urldata.url);
                    return;
                }
            }
        } else {
            this.setupCurConfig();
        }
    }

    /**
    * Allows setting of preferences or configuration (including extensions).
    * @function module:SVGEditor.setConfig
    * @param {module:SVGEditor.Config|module:SVGEditor.Prefs} opts The preferences or configuration (including extensions). See the tutorial on {@tutorial ConfigOptions} for info on config and preferences.
    * @param {PlainObject} [cfgCfg] Describes configuration which applies to the
    *    particular batch of supplied options
    * @param {boolean} [cfgCfg.allowInitialUserOverride=false] Set to true if you wish
    *  to allow initial overriding of settings by the user via the URL
    *  (if permitted) or previously stored preferences (if permitted);
    *  note that it will be too late if you make such calls in extension
    *  code because the URL or preference storage settings will
    *   have already taken place.
    * @param {boolean} [cfgCfg.overwrite=true] Set to false if you wish to
    *  prevent the overwriting of prior-set preferences or configuration
    *  (URL settings will always follow this requirement for security
    *  reasons, so `svgedit-config-iife.js` settings cannot be overridden unless it
    *  explicitly permits via `allowInitialUserOverride` but extension config
    *  can be overridden as they will run after URL settings). Should
    *   not be needed in `svgedit-config-iife.js`.
    * @returns {void}
  */
    setConfig(opts, cfgCfg: any = {}) {
        /**
         *
         * @param {module:SVGEditor.Config|module:SVGEditor.Prefs} cfgObj
         * @param {string} key
         * @param {any} val See {@link module:SVGEditor.Config} or {@link module:SVGEditor.Prefs}
         * @returns {void}
         */
        const extendOrAdd = (cfgObj, key, val) => {
            if (cfgObj[key] && typeof cfgObj[key] === 'object') {
                cfgObj[key] = mergeDeep(cfgObj[key], val);
            } else {
                cfgObj[key] = val;
            }
        };
        Object.entries<string>(opts).forEach(([key, val]) => {
            // Only allow prefs defined in configObj.defaultPrefs or...
            if (key in this.defaultPrefs) {
                if (cfgCfg.overwrite === false && (
                    this.curConfig.preventAllURLConfig ||
                    this.curPrefs[key])
                ) {
                    return;
                }
                if (cfgCfg.allowInitialUserOverride === true) {
                    this.defaultPrefs[key] = val;
                } else {
                    this.pref(key, val);
                }
            } else if (['userExtensions', 'allowedOrigins'].includes(key)) {
                if (cfgCfg.overwrite === false &&
                    (
                        this.curConfig.preventAllURLConfig ||
                        ['allowedOrigins'].includes(key)
                    )
                ) {
                    return;
                }
                this.curConfig[key] = this.curConfig[key].concat(val); // We will handle any dupes later
                // Only allow other configObj.curConfig if defined in configObj.defaultConfig
            } else if ({}.hasOwnProperty.call(this.defaultConfig, key)) {
                if (cfgCfg.overwrite === false && (
                    this.curConfig.preventAllURLConfig ||
                    {}.hasOwnProperty.call(this.curConfig, key)
                )) {
                    return;
                }
                // Potentially overwriting of previously set config
                if ({}.hasOwnProperty.call(this.curConfig, key)) {
                    if (cfgCfg.overwrite === false) {
                        return;
                    }
                    extendOrAdd(this.curConfig, key, val);
                } else if (cfgCfg.allowInitialUserOverride === true) {
                    extendOrAdd(this.defaultConfig, key, val);
                } else if (this.defaultConfig[key] && typeof this.defaultConfig[key] === 'object') {
                    this.curConfig[key] = Array.isArray(this.defaultConfig[key]) ? [] : {};
                    this.curConfig[key] = mergeDeep(this.curConfig[key], val);
                } else {
                    this.curConfig[key] = val;
                }
            }
        });
    }
    /**
    * Store and retrieve preferences.
    * @function pref
    * @param {string} key The preference name to be retrieved or set
    * @param {string} [val] The value. If the value supplied is missing or falsey, no change to the preference will
    * be made unless `mayBeEmpty` is set.
    * @param {boolean} [mayBeEmpty] If value may be falsey.
    * @returns {string|void} If val is missing or falsey and `mayBeEmpty` is not set, the
    * value of the previously stored preference will be returned.
    * @todo Review whether any remaining existing direct references to
    *  getting `curPrefs` can be changed to use `svgEditor.configObj.pref()` getting to ensure
    *  `defaultPrefs` fallback (also for sake of `allowInitialUserOverride`);
    *  specifically, `bkgd_color` could be changed so that the pref dialog has a
    *  button to auto-calculate background, but otherwise uses `svgEditor.configObj.pref()` to
    *  be able to get default prefs or overridable settings
    */
    pref(key: string, val?: string, mayBeEmpty?: boolean) {
        if (mayBeEmpty || val !== undefined) {
            this.curPrefs[key] = val;
            window.localStorage.setItem(key, val);
        }

        return (key in this.curPrefs) ? this.curPrefs[key] : this.defaultPrefs[key];
    }

    getPref(key: string, defaultValue: any) {
        return this.pref(key) ?? defaultValue;
    }

    getBoolPref(key: string, defaultValue: boolean = false) {
        const v = this.pref(key);
        if(v) {
            return v.toString().trim().toLowerCase() === "true";
        }
        return defaultValue;
    }

    /**
     * @function load load Config
     * @returns {void}
     */
    load() {
        this.loadFromURL();
        this.setupCurPrefs();
    }
}
